import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { CartItem } from '../types/cart';
import { Product } from '../types/product';
import { CustomizationData } from '../types/customization';
import { useAuth } from './AuthContext';
import { saveCartToFirebase, loadCartFromFirebase } from '../services/database';

const LOCAL_STORAGE_CART_KEY = 'quirke_trophies_guest_cart';

interface CartContextType {
  items: CartItem[];
  addItem: (product: Product, quantity: number, customizationData?: CustomizationData, ribbonProduct?: Product) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  totalAmount: number;
  totalItems: number;
  generateOrderSummary: () => string;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

// Helper functions for localStorage
const saveCartToLocalStorage = (items: CartItem[]) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_CART_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

const loadCartFromLocalStorage = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

const clearLocalStorageCart = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
  } catch (error) {
    console.error('Error clearing localStorage cart:', error);
  }
};

// Merge guest cart with Firebase cart (avoid duplicates by product id)
const mergeCartItems = (firebaseItems: CartItem[], localItems: CartItem[]): CartItem[] => {
  const merged = [...firebaseItems];
  localItems.forEach((localItem) => {
    const existingIndex = merged.findIndex((item) => item.product.id === localItem.product.id);
    if (existingIndex >= 0) {
      // Add quantities if product already exists
      merged[existingIndex] = {
        ...merged[existingIndex],
        quantity: merged[existingIndex].quantity + localItem.quantity,
      };
    } else {
      merged.push(localItem);
    }
  });
  return merged;
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const { currentUser } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const previousUserRef = useRef<string | null>(null);

  // Load cart on mount and handle user login/logout
  useEffect(() => {
    const loadCart = async () => {
      if (currentUser) {
        // User is logged in
        const localCart = loadCartFromLocalStorage();
        
        try {
          const firebaseCart = await loadCartFromFirebase(currentUser.uid);
          
          // Check if user just logged in (was previously not logged in)
          if (previousUserRef.current === null && localCart.length > 0) {
            // Merge local cart with Firebase cart
            const mergedCart = mergeCartItems(firebaseCart, localCart);
            setItems(mergedCart);
            // Save merged cart to Firebase
            await saveCartToFirebase(currentUser.uid, mergedCart);
            // Clear localStorage after merge
            clearLocalStorageCart();
          } else {
            setItems(firebaseCart);
          }
        } catch (error) {
          console.error('Error loading cart:', error);
          // Fallback to local cart if Firebase fails
          setItems(localCart);
        }
        
        previousUserRef.current = currentUser.uid;
      } else {
        // User is not logged in - use localStorage
        const localCart = loadCartFromLocalStorage();
        setItems(localCart);
        previousUserRef.current = null;
      }
      setIsInitialized(true);
    };

    loadCart();
  }, [currentUser]);

  // Save cart on change (after initial load)
  useEffect(() => {
    if (!isInitialized) return;
    
    if (currentUser) {
      // Save to Firebase for logged-in users
      saveCartToFirebase(currentUser.uid, items).catch((error) => {
        console.error('Error saving cart to Firebase:', error);
      });
    } else {
      // Save to localStorage for guest users
      saveCartToLocalStorage(items);
    }
  }, [items, currentUser, isInitialized]);

  const addItem = (product: Product, quantity: number, customizationData?: CustomizationData, ribbonProduct?: Product) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { product, quantity, customizationData, ribbonProduct }];
    });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    setItems((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const removeItem = (productId: string) => {
    setItems((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const totalAmount = items.reduce(
    (sum, item) => sum + (item.product.price || 0) * item.quantity,
    0
  );

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const generateOrderSummary = (): string => {
    let summary = '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';
    summary += '🛒 QUIRKE TROPHIES - ORDER DETAILS\n';
    summary += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n';
    summary += 'Order Items:\n';

    items.forEach((item, index) => {
      const itemTotal = (item.product.price || 0) * item.quantity;
      summary += `${index + 1}. ${item.product.name} (${item.product.sku || item.product.productId}) x${item.quantity}\n`;
      summary += `   Price: €${(item.product.price || 0).toFixed(2)} each\n`;

      // Include customization data if present
      if (item.customizationData) {
        summary += '   Personalization:\n';
        Object.entries(item.customizationData).forEach(([key, value]) => {
          if (value && value.toString().trim()) {
            summary += `     - ${key}: ${value}\n`;
          }
        });
      }

      // Include ribbon product if present
      if (item.ribbonProduct) {
        summary += `   Ribbon: ${item.ribbonProduct.name}\n`;
      }

      summary += `   Subtotal: €${itemTotal.toFixed(2)}\n\n`;
    });

    summary += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';
    summary += `Total Items: ${totalItems}\n`;
    summary += `TOTAL AMOUNT: €${totalAmount.toFixed(2)}\n`;
    summary += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━';

    return summary;
  };

  const value = {
    items,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    totalAmount,
    totalItems,
    generateOrderSummary,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
